import {
  ChangeDetectionStrategy,
  Component,
  inject,
  linkedSignal,
  Signal,
} from '@angular/core';

import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';

import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import {
  GridColumns,
  mapGridColumns,
  WcgButtonComponent,
  WcgDialogComponent,
  WcgDragDropWrapperDirective,
} from '@wcg/frontend-framework';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatCheckboxModule,
    MatIconModule,
    WcgButtonComponent,
    WcgDialogComponent,
    WcgDragDropWrapperDirective,
    DragDropModule,
    CdkDrag,
    CdkDropList,
  ],
  selector: 'wcg-manage-table-dialog',
  styleUrls: ['./manage-table.dialog.component.scss'],
  template: `
    <wcg-dialog title="Manage Table">
      <div content class="w-full">
        <p class="body-large">
          Select the columns you want to view and rearrange the order.
        </p>
        <wcg-drag-drop-wrapper
          (handleDrop)="handleDropChange($event)"
          orientation="vertical"
          lockAxis="y"
        >
          @for (column of manageableColumns(); track column; let i = $index) {
            <div>
              <div
                class="flex flex-row justify-between  items-center py-5"
                cdkDrag
                [cdkDragData]="column"
              >
                <div class="drag-row">
                  <mat-checkbox
                    wcg-checkbox
                    color="primary"
                    [checked]="column.selected"
                    (change)="column.selected = $event.checked"
                    >{{ column.label }}</mat-checkbox
                  >
                </div>
                <div class="drag-row">
                  <mat-icon class="drag-indicator">drag_indicator</mat-icon>
                </div>
              </div>
            </div>
          }
        </wcg-drag-drop-wrapper>
      </div>
      <div tertiary>
        <wcg-button color="tertiary" (click)="cancel()">Cancel</wcg-button>
      </div>
      <div primary>
        <wcg-button color="primary" (click)="save()">Save</wcg-button>
      </div>
    </wcg-dialog>
  `,
})
export class ManageTableDialogComponent {
  private readonly _dialogRef = inject(
    MatDialogRef<ManageTableDialogComponent>,
  );
  private readonly _data = inject<Signal<GridColumns>>(MAT_DIALOG_DATA);

  private _columnKeys = linkedSignal({
    computation: (columns) => Object.keys(columns!),
    source: this._data,
  });

  manageableColumns = linkedSignal({
    computation: (columns) =>
      Object.values(columns!).filter(
        (col) =>
          col.componentType !== 'action' && col.componentType !== 'checkbox',
      ),
    source: this._data,
  });
  handleDropChange(event: CdkDragDrop<GridColumns>) {
    this.manageableColumns.update((columns) => {
      const currentOrder = [...columns];
      moveItemInArray(currentOrder, event.previousIndex, event.currentIndex);
      return [...currentOrder];
    });
  }

  save(): void {
    this._dialogRef.close(mapGridColumns(this.manageableColumns()));
  }

  cancel(): void {
    this._dialogRef.close();
  }
}
